import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import shiftService from '../services/shiftService';

const ShiftAssignmentPage = () => {
  const { user } = useAuth();
  const canAccessShiftAssignment = ['HRO', 'CHAIRMAN'].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    hrId: '',
    shiftType: '',
    startDate: '',
    endDate: ''
  });
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    shiftType: '',
    startDate: '',
    endDate: ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  useEffect(() => {
    if (canAccessShiftAssignment) {
      fetchData();
    }
  }, [canAccessShiftAssignment]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Load all available shifts
      const shiftsResponse = await shiftService.getAllShifts();
      if (shiftsResponse.success && shiftsResponse.data.length > 0) {
        setShifts(shiftsResponse.data);
        // Set default shift type to the first available shift name
        setFormData((prev) => ({ ...prev, shiftType: shiftsResponse.data[0].shiftName }));
      }
      
      // Load HR employees
      const employeesResponse = await shiftService.getHREmployeesForShiftAssignment();
      
      if (employeesResponse.success) {
        setEmployees(employeesResponse.data);
        if (employeesResponse.data.length > 0) {
          const initialHrId = formData.hrId || employeesResponse.data[0]._id;
          setFormData((prev) => ({ ...prev, hrId: initialHrId }));
          await fetchTimeline(initialHrId);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async (hrId) => {
    if (!hrId) {
      setTimeline([]);
      return;
    }

    try {
      setTimelineLoading(true);
      const response = await shiftService.getHRTimeline(hrId);
      if (response.success) {
        setTimeline(response.data);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load shift timeline');
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'hrId') {
      fetchTimeline(value);
    }
  };

  const handleAssignShift = async () => {
    if (!formData.hrId || !formData.shiftType || !formData.startDate || !formData.endDate) {
      setError('Please fill all fields to assign a shift');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await shiftService.assignShiftWithDateRange(formData);
      if (response.success) {
        const selectedEmployee = employees.find((employee) => employee._id === formData.hrId);
        setSuccess(`Shift assigned to ${getEmployeeName(selectedEmployee)} successfully!`);
        await fetchData();
        await fetchTimeline(formData.hrId);
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = Array.isArray(details) && details.length > 0
        ? (details[0].msg || details[0].message)
        : '';

      setError(detailMessage || err.response?.data?.error || err.message || 'Failed to assign shift');
    }
  };

  const handleEditClick = (shift) => {
    setEditingAssignmentId(shift._id);
    setEditFormData({
      shiftType: shift.shiftType,
      startDate: shift.startDate,
      endDate: shift.endDate
    });
    setError('');
    setSuccess('');
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateShift = async () => {
    if (!editFormData.shiftType || !editFormData.startDate || !editFormData.endDate) {
      setError('Please fill all fields to update the shift');
      return;
    }

    try {
      setError('');
      setSuccess('');

      const response = await shiftService.updateShiftAssignment(editingAssignmentId, editFormData);
      if (response.success) {
        setSuccess('Shift updated successfully!');
        setEditingAssignmentId(null);
        await fetchTimeline(formData.hrId);
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = Array.isArray(details) && details.length > 0
        ? (details[0].msg || details[0].message)
        : '';

      setError(detailMessage || err.response?.data?.error || err.message || 'Failed to update shift');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setError('');
      setSuccess('');

      const response = await shiftService.deleteShiftAssignment(showDeleteConfirm);
      if (response.success) {
        setSuccess('Shift deleted successfully!');
        setShowDeleteConfirm(null);
        await fetchTimeline(formData.hrId);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to delete shift');
      setShowDeleteConfirm(null);
    }
  };

  const formatDate = (date) => {
    if (!date) {
      return 'N/A';
    }

    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getEmployeeName = (employee) => {
    if (!employee) return 'HR Employee';

    const name = employee.name || employee.fullName;
    if (name && String(name).trim()) return String(name).trim();

    const employeeCode = employee.employeeId || employee.jobId || employee.username;
    if (employeeCode && String(employeeCode).trim()) return `Employee ${String(employeeCode).trim()}`;

    return employee.phoneNumber || employee.email || 'HR Employee';
  };

  if (!canAccessShiftAssignment) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-gray-600">Access Denied</h2>
            <p className="text-gray-500 mt-2">This page is only accessible to HRO and Chairman.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Shift Assignment</h1>
          <p className="text-gray-600">Assign date-range shifts and view timeline history</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6 h-fit">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Assign Shift</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select HR</label>
                <select
                  name="hrId"
                  value={formData.hrId}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select HR employee</option>
                  {employees.map((employee) => (
                    <option key={employee._id} value={employee._id}>
                      {getEmployeeName(employee)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shift Type</label>
                <select
                  name="shiftType"
                  value={formData.shiftType}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a shift type</option>
                  {shifts.map((shift) => (
                    <option key={shift._id} value={shift.shiftName}>
                      {shift.shiftName} ({shift.startTime} - {shift.endTime})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleFormChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                type="button"
                onClick={handleAssignShift}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Assign Shift Range
              </button>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-lg shadow-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Shift Timeline</h2>
              <p className="text-sm text-gray-600 mt-1">All shifts for selected HR employee, sorted by date</p>
            </div>

            {loading || timelineLoading ? (
              <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 mt-2">Loading timeline...</p>
              </div>
            ) : !formData.hrId ? (
              <div className="p-6 text-center text-gray-500">Select an HR employee to view timeline.</div>
            ) : timeline.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No shifts assigned for this HR employee.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {timeline.map((entry) => (
                      <tr key={entry._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {entry.shiftType}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(entry.startDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(entry.endDate)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                          <button
                            onClick={() => handleEditClick(entry)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(entry._id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Edit Shift Modal */}
        {editingAssignmentId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Edit Shift Assignment</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Shift Type</label>
                  <select
                    name="shiftType"
                    value={editFormData.shiftType}
                    onChange={handleEditFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a shift type</option>
                    {shifts.map((shift) => (
                      <option key={shift._id} value={shift.shiftName}>
                        {shift.shiftName} ({shift.startTime} - {shift.endTime})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={editFormData.startDate}
                    onChange={handleEditFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={editFormData.endDate}
                    onChange={handleEditFormChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={handleUpdateShift}
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setEditingAssignmentId(null)}
                  className="flex-1 bg-gray-300 text-gray-900 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Confirm Delete</h2>
              <p className="text-gray-600 mb-6">Are you sure you want to delete this shift assignment? This action cannot be undone.</p>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleDeleteConfirm}
                  className="flex-1 bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-gray-300 text-gray-900 py-2 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Current Shift Overview</h2>
          </div>

          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading employees...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Employee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Shift Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Active Range
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {employees.map((employee) => (
                    <tr key={employee._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{employee.phoneNumber}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{employee.email || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {employee.currentShift ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {employee.currentShift.shiftType}
                            </div>
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            No Shift Assigned
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {employee.currentShift
                            ? `${formatDate(employee.currentShift.startDate)} to ${formatDate(employee.currentShift.endDate)}`
                            : 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Shift Statistics */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Total HR Employees</h3>
            <p className="text-3xl font-bold text-blue-600">{employees.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Currently Assigned</h3>
            <p className="text-3xl font-bold text-green-600">
              {employees.filter((emp) => emp.currentShift).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Unassigned Employees</h3>
            <p className="text-3xl font-bold text-red-600">
              {employees.filter((emp) => !emp.currentShift).length}
            </p>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ShiftAssignmentPage;